/**
 * Canonical registry definitions for Swan's Domains. Behaviour and Recognition
 * retain protected runtime bindings, but they participate in the same central
 * Prototype and configuration model as every other Domain.
 *
 * This module is deliberately pure: browser registry code, catalog validation,
 * tests, and migration tooling consume the same definitions without importing
 * Firebase.
 */

import {
  attendanceDomainConfig,
  dailyRegisterDomainConfig,
  periodRegisterDomainConfig,
} from './configs/attendanceDomainConfig.js';
import { participationDomainConfig } from './configs/participationDomainConfig.js';
import { healthDomainConfig } from './configs/healthDomainConfig.js';
import { academicsDomainConfig } from './configs/academicsDomainConfig.js';
import { incidentReportsDomainConfig } from './configs/incidentReportsDomainConfig.js';
import { policiesDomainConfig } from './configs/policiesDomainConfig.js';

export const PROTECTED_DOMAIN_IDS = Object.freeze(['discipline', 'recognition']);

/**
 * Compatibility export for callers that still need to select specialised
 * Behaviour/Recognition runtimes. It is intentionally empty: runtime
 * protection is not an exclusion from central governance.
 */
export const CENTRAL_GOVERNANCE_EXCLUDED_DOMAIN_IDS = Object.freeze([]);

/**
 * Keys stored per school for a centrally defined Domain. Everything else in
 * the effective Domain comes from the current central definition.
 */
export const SCHOOL_SETTABLE_PROTECTED_KEYS = Object.freeze([
  'storiesEnabled',
  'active',
]);

export const SCHOOL_SETTABLE_GOVERNED_PATHS = Object.freeze([
  'storiesEnabled',
  'reporting.parentWeeklySummary',
  'reporting.staffWeeklyBrief',
]);

export const ALWAYS_ON_PROTECTED_IDS = PROTECTED_DOMAIN_IDS;

export const PROTECTED_DOMAIN_DEFINITIONS = Object.freeze({
  discipline: {
    name: 'discipline',
    label: 'Behaviour',
    description: 'Behaviour incidents and consequences',
    protected: true,
    active: true,
    sortOrder: 1,
    icon: null,
    color: 'error',
    defaultInputMethod: 'individual',
    collections: {
      events: 'mentions',
      rules: 'disciplineRules',
      types: 'mentionTypes',
      categories: 'disciplineCategories',
      actions: 'consequenceTypes',
      queue: 'disciplineQueue',
    },
    eventsCollection: 'mentions',
    // Instances live in the Queue; collections.actions retains the historical
    // action-definition binding. Consumers need the same explicit instance key.
    actionsCollection: 'disciplineQueue',
    rulesCollection: 'disciplineRules',
    terminology: {
      event: { label: 'mention', plural: 'mentions', description: 'A behaviour incident' },
      category: { label: 'offence', plural: 'offences', description: 'Type of behaviour' },
      type: { label: 'mention type', plural: 'mention types', description: '' },
      action: { label: 'consequence', plural: 'consequences', description: 'Action taken in response' },
      rule: { label: 'rule', plural: 'rules', description: '' },
      queue: { label: 'consequence queue', plural: 'consequence queues', description: '' },
    },
    contextTags: {},
    scope: { grades: [] },
  },
  recognition: {
    name: 'recognition',
    label: 'Recognition',
    description: 'Positive recognition aligned with school values',
    protected: true,
    active: true,
    sortOrder: 2,
    icon: null,
    color: 'success',
    defaultInputMethod: 'individual',
    collections: {
      events: 'recognitions',
      rules: 'recognitionRules',
      types: 'recognitionTypes',
      categories: 'values',
    },
    eventsCollection: 'recognitions',
    rulesCollection: 'recognitionRules',
    terminology: {
      event: { label: 'recognition', plural: 'recognitions', description: 'A moment of recognition' },
      category: { label: 'value', plural: 'values', description: 'Underlying value being recognized' },
      type: { label: 'recognition type', plural: 'recognition types', description: '' },
      action: { label: 'reward', plural: 'rewards', description: '' },
      rule: { label: 'rule', plural: 'rules', description: '' },
    },
    contextTags: {},
    scope: { grades: [] },
  },
});

/** Centrally supplied configurations for standard (non-specialised-runtime) Domains. */
export const CENTRAL_GOVERNED_DOMAIN_CONFIGS = Object.freeze({
  attendance: attendanceDomainConfig,
  'daily-register': dailyRegisterDomainConfig,
  'period-register': periodRegisterDomainConfig,
  participation: participationDomainConfig,
  'incident-reports': incidentReportsDomainConfig,
  health: healthDomainConfig,
  academics: academicsDomainConfig,
  policies: policiesDomainConfig,
});

export const REGISTRY_MANAGED_DOMAIN_CONFIGS = Object.freeze({
  ...PROTECTED_DOMAIN_DEFINITIONS,
  ...CENTRAL_GOVERNED_DOMAIN_CONFIGS,
});

export const REGISTRY_MANAGED_DOMAIN_IDS = Object.freeze(
  Object.keys(REGISTRY_MANAGED_DOMAIN_CONFIGS),
);
